import React, { ReactNode } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { V2App } from './V2App';
import { useCockpit } from './data/CockpitContext';
import { v2 } from './paths';
import { DashboardPage } from './pages/DashboardPage';
import { EnvironmentsPage } from './pages/EnvironmentsPage';
import { EnvironmentPage } from './pages/EnvironmentPage';
import { IssuesPage } from './pages/IssuesPage';
import { IssuePanel } from './pages/IssuePanel';
import { DeployPage } from './pages/DeployPage';
import { MonitorPage } from './pages/MonitorPage';
import { EnvExactPage, ExactPage } from './pages/ExactPage';
import { EnvLayout } from './pages/EnvLayout';
import { ObjectPage } from './pages/ObjectPage';
import { ScenariosPage } from './pages/ScenariosPage';
import { RunPage } from './pages/RunPage';
import { LearnPage } from './pages/LearnPage';
import { ErrorLogPage } from './pages/ErrorLogPage';

/** Support only; a customer account lands on its own error log. The API checks the role again. */
const Admin = ({ children }: { children: ReactNode }) => {
    const { isAdmin } = useCockpit();
    return isAdmin ? <>{children}</> : <Home />;
};

const Home = () => {
    const { isAdmin, environments } = useCockpit();
    if (isAdmin) return <DashboardPage />;
    const first = environments.find((e) => e.environment.toLowerCase().startsWith('prod')) ?? environments[0];
    return first ? <Navigate replace to={v2.errorLog(first.customer, first.environment)} /> : <div className="empty">Geen omgevingen voor dit account.</div>;
};

/** Every v2 route, under /v2 next to the old pages. */
export const v2Routes = (
    <Route path="/v2" element={<V2App />}>
        <Route index element={<Home />} />
        <Route path="omgevingen" element={<Admin><EnvironmentsPage /></Admin>} />
        <Route path="issues" element={<Admin><IssuesPage /></Admin>} />
        <Route path="issue/:customer/:environment/:key" element={<Admin><IssuePanel /></Admin>} />
        <Route path="deploy" element={<Admin><DeployPage /></Admin>} />
        <Route path="deploy/:deployId" element={<Admin><DeployPage /></Admin>} />
        <Route path="exact" element={<Admin><ExactPage /></Admin>} />
        <Route path="exact/:customer/:environment" element={<Admin><ExactPage /></Admin>} />
        <Route path="scenarios" element={<Admin><ScenariosPage /></Admin>} />
        <Route path="scenarios/:customer/:environment/:runId" element={<Admin><RunPage /></Admin>} />
        <Route path="leren" element={<Admin><LearnPage /></Admin>} />
        <Route path="leren/klantmails" element={<Admin><LearnPage tab="mails" /></Admin>} />
        {/* One frame for every tab of an environment; the error log is the only tab a customer sees. */}
        <Route path="env/:customer/:environment" element={<EnvLayout />}>
            <Route index element={<Admin><EnvironmentPage /></Admin>} />
            <Route path="issues" element={<Admin><IssuesPage /></Admin>} />
            <Route path="issues/:key" element={<Admin><IssuePanel /></Admin>} />
            <Route path="monitor" element={<Admin><MonitorPage /></Admin>} />
            <Route path="exact" element={<Admin><EnvExactPage /></Admin>} />
            <Route path="object/:objectId" element={<Admin><ObjectPage /></Admin>} />
            <Route path="foutenlogboek" element={<ErrorLogPage />} />
        </Route>
        <Route path="*" element={<Navigate replace to={v2.home()} />} />
    </Route>
);
